import { Observable, of } from 'rxjs';
import { take, map, catchError } from 'rxjs/operators';

/**
 * Shared init for notifications list/detail: load locations (admin) or employee profile (staff),
 * set selected location, then load notifications. Use in ngOnInit when no route-specific notification ID.
 */
export function runNotificationsInit(options: {
  isAdmin: boolean;
  loadLocations: () => Observable<any>;
  getCurrentEmployeeProfile: () => Observable<any>;
  onAdminSuccess: () => void;
  onStaffSuccess: (response: any) => void;
  onError: () => void;
}): void {
  if (options.isAdmin) {
    options.loadLocations().subscribe({
      next: () => options.onAdminSuccess(),
      error: () => options.onError()
    });
  } else {
    options.getCurrentEmployeeProfile().subscribe({
      next: (response) => options.onStaffSuccess(response),
      error: () => options.onError()
    });
  }
}

export interface LocationOptionForNotifications {
  id: string;
  name: string;
}

/**
 * Load locations for admin notifications list/detail. Returns an Observable that updates
 * the given setter with mapped locations. Use as: loadLocationsForNotificationsList(this.locationService, (locs) => this.availableLocations = locs).
 */
export function loadLocationsForNotificationsList(
  locationService: { getLocations(): Observable<any> },
  setAvailableLocations: (locs: LocationOptionForNotifications[]) => void
): Observable<LocationOptionForNotifications[]> {
  return locationService.getLocations().pipe(
    take(1),
    map((response: any) => {
      if (response.status && response.data && Array.isArray(response.data)) {
        const locations = response.data.map((location: any) => ({
          id: location.id,
          name: location.branch || location.name || 'Unknown Location'
        }));
        setAvailableLocations(locations);
        return locations;
      }
      return [];
    }),
    catchError(error => {
      console.error('Error fetching locations:', error);
      return of([]);
    })
  );
}
